/**
 * Custom Cart JavaScript
 * Handles cart operations: update quantity, remove items, refresh cart
 */

class CartCustom {
  constructor() {
    this.cartItems = document.querySelectorAll('[data-cart-item]');
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Close cart button
    const closeButton = document.querySelector('.cart-custom-close');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        window.history.back();
      });
    }

    // Quantity change buttons
    document.querySelectorAll('[data-quantity-change]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const change = parseInt(button.dataset.quantityChange);
        const cartItem = button.closest('[data-cart-item]');
        const input = cartItem.querySelector('[data-quantity-input]');
        const newQuantity = Math.max(1, parseInt(input.value) + change);
        this.updateQuantity(cartItem, newQuantity);
      });
    });

    // Quantity input direct change
    document.querySelectorAll('[data-quantity-input]').forEach(input => {
      input.addEventListener('change', (e) => {
        const cartItem = input.closest('[data-cart-item]');
        const newQuantity = Math.max(1, parseInt(input.value) || 1);
        input.value = newQuantity;
        this.updateQuantity(cartItem, newQuantity);
      });

      // Prevent non-numeric input
      input.addEventListener('keypress', (e) => {
        if (e.which < 48 || e.which > 57) {
          e.preventDefault();
        }
      });
    });

    // Remove item buttons
    document.querySelectorAll('[data-remove-item]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const cartItem = button.closest('[data-cart-item]');
        this.removeItem(cartItem);
      });
    });
  }

  async updateQuantity(cartItem, quantity) {
    const line = parseInt(cartItem.dataset.line);
    const input = cartItem.querySelector('[data-quantity-input]');
    
    // Add loading state
    cartItem.classList.add('updating');
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      
      // Update UI
      this.updateCartUI(cart);
      
      // Update input value
      input.value = quantity;
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      // Revert input value on error
      input.value = input.defaultValue;
      alert('שגיאה בעדכון העגלה. אנא נסה שוב.');
    } finally {
      cartItem.classList.remove('updating');
    }
  }

  async removeItem(cartItem) {
    const line = parseInt(cartItem.dataset.line);
    
    // Add loading state
    cartItem.classList.add('updating');
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      
      // Remove item from DOM with animation
      cartItem.style.transition = 'opacity 0.3s, transform 0.3s';
      cartItem.style.opacity = '0';
      cartItem.style.transform = 'translateX(-20px)';
      
      setTimeout(() => {
        cartItem.remove();
        
        // Update UI
        this.updateCartUI(cart);
        
        // Check if cart is empty and reload page
        if (cart.item_count === 0) {
          window.location.reload();
        }
      }, 300);
      
    } catch (error) {
      console.error('Error removing item:', error);
      cartItem.classList.remove('updating');
      alert('שגיאה בהסרת הפריט. אנא נסה שוב.');
    }
  }

  updateCartUI(cart) {
    // Update total price
    const totalElement = document.querySelector('[data-cart-total]');
    if (totalElement) {
      const totalPrice = (cart.total_price / 100).toFixed(2);
      totalElement.textContent = `₪${totalPrice}`;
    }

    // Update cart count in badge
    const countText = document.querySelector('.cart-count-text');
    if (countText) {
      countText.textContent = cart.item_count;
    }

    // Update item prices
    cart.items.forEach((item, index) => {
      const cartItem = document.querySelector(`[data-line="${index + 1}"]`);
      if (cartItem) {
        const priceElement = cartItem.querySelector('.cart-item-price .price');
        if (priceElement) {
          const itemPrice = (item.final_line_price / 100).toFixed(2);
          priceElement.textContent = `₪${itemPrice}`;
        }
      }
    });

    // Update free shipping message
    const messageContainer = document.querySelector('.cart-custom-message');
    const messageElement = messageContainer?.querySelector('p');
    if (messageElement && messageContainer) {
      const threshold = parseFloat(messageContainer.dataset.freeShippingThreshold) || 250;
      const freeShippingMessage = messageContainer.dataset.freeShippingMessage || 'זכאי למשלוח חינם! 🎉';
      const remaining = Math.max(0, threshold - (cart.total_price / 100));
      
      // Preserve the inline color style
      const currentColor = messageElement.style.color;
      
      if (remaining > 0) {
        messageElement.textContent = `הוסף עוד ₪${remaining.toFixed(2)} למשלוח חינם`;
      } else {
        messageElement.textContent = freeShippingMessage;
      }
      
      // Reapply color if it exists
      if (currentColor) {
        messageElement.style.color = currentColor;
      }
      
      // Update progress bar
      const progressBar = messageContainer.querySelector('.shipping-progress-fill');
      if (progressBar) {
        const progressPercent = Math.min(100, ((cart.total_price / 100) / threshold) * 100);
        progressBar.style.width = `${progressPercent}%`;
      }
    }

    // Trigger cart update event for theme
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));
  }
}

// Add quick product to cart
window.addQuickProduct = async function(variantId, event) {
  // Get button from event or fallback to event.target
  const button = event?.currentTarget || event?.target || document.activeElement;
  const originalText = button?.textContent || 'הוסף';
  
  // Show loading state
  if (button) {
    button.textContent = '...';
    button.disabled = true;
  }
  
  try {
    // Ensure variantId is a number
    const id = parseInt(variantId);
    if (isNaN(id)) {
      throw new Error('Invalid variant ID');
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: id,
          quantity: 1
        }]
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || 'Failed to add product');
    }

    // Reload page to show updated cart
    window.location.reload();
    
  } catch (error) {
    console.error('Error adding quick product:', error);
    if (button) {
      button.textContent = originalText;
      button.disabled = false;
    }
    alert('שגיאה בהוספת המוצר. אנא נסה שוב.');
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartCustom();
  });
} else {
  new CartCustom();
}
